import { useCallback, useEffect, useRef, useState } from 'react';
import { FLOWCHART_VISUAL_DOT_PITCH } from '../../SprintDetail/FlowchartView/components/FlowchartCanvas/constants';

const MIN_ZOOM = 0.28;
const MAX_ZOOM = 1.8;
/** Folder names are 11px in world space; Flow mission titles are 13px at zoom 1. */
const DEFAULT_ZOOM = 13 / 11;
const FIT_MAX_ZOOM = DEFAULT_ZOOM;
const WHEEL_COMMIT_MS = 80;

/**
 * Pan/zoom for the Atlas canvas. Gestures write a camera layer in the DOM;
 * React state commits when the gesture ends so cards are not re-rendered
 * every pointermove.
 */
export function useAtlasViewport({ containerRef, minimapApplyRef } = {}) {
  const [pan, setPan] = useState({ x: 40, y: 40 });
  const [zoom, setZoom] = useState(DEFAULT_ZOOM);
  const panRef = useRef(pan);
  const zoomRef = useRef(zoom);
  const dragRef = useRef(null);
  const animationRef = useRef(null);
  const wheelCommitRef = useRef(null);
  const gesturingRef = useRef(false);
  const cameraRef = useRef(null);
  const gridRef = useRef(null);
  /** Survives pointerup so the following click does not clear selection. */
  const didPanRef = useRef(false);

  const cancelCameraAnim = useCallback(() => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
  }, []);

  const applyCameraDom = useCallback((nextPan, nextZoom) => {
    const z = nextZoom;
    const p = nextPan;
    panRef.current = p;
    zoomRef.current = z;
    const cam = cameraRef.current;
    if (cam) {
      cam.style.transform = `translate(${p.x}px, ${p.y}px) scale(${z})`;
      cam.style.setProperty('--atlas-zoom', String(z));
    }
    const grid = gridRef.current;
    if (grid) {
      const pitch = FLOWCHART_VISUAL_DOT_PITCH * z;
      grid.style.backgroundSize = `${pitch}px ${pitch}px`;
      grid.style.backgroundPosition = `${p.x}px ${p.y}px`;
    }
    minimapApplyRef?.current?.(p.x, p.y, z);
  }, [minimapApplyRef]);

  const commitCamera = useCallback(() => {
    setPan(panRef.current);
    setZoom(zoomRef.current);
  }, []);

  const applyCamera = useCallback((nextPan, nextZoom) => {
    applyCameraDom(nextPan, nextZoom);
    setZoom(nextZoom);
    setPan(nextPan);
  }, [applyCameraDom]);

  const animateCamera = useCallback((targetPan, targetZoom, duration = 320) => {
    cancelCameraAnim();
    const startPan = { ...panRef.current };
    const startZoom = zoomRef.current;
    if (
      Math.abs(targetZoom - startZoom) < 0.002 &&
      Math.abs(targetPan.x - startPan.x) < 1 &&
      Math.abs(targetPan.y - startPan.y) < 1
    ) {
      applyCamera(targetPan, targetZoom);
      return;
    }
    gesturingRef.current = true;
    const startTime = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - startTime) / duration);
      const k = 1 - (1 - t) ** 3;
      applyCameraDom(
        {
          x: startPan.x + (targetPan.x - startPan.x) * k,
          y: startPan.y + (targetPan.y - startPan.y) * k,
        },
        startZoom + (targetZoom - startZoom) * k
      );
      if (t < 1) {
        animationRef.current = requestAnimationFrame(tick);
        return;
      }
      animationRef.current = null;
      gesturingRef.current = false;
      commitCamera();
    };
    animationRef.current = requestAnimationFrame(tick);
  }, [applyCamera, applyCameraDom, cancelCameraAnim, commitCamera]);

  useEffect(() => () => {
    cancelCameraAnim();
    if (wheelCommitRef.current) window.clearTimeout(wheelCommitRef.current);
  }, [cancelCameraAnim]);

  useEffect(() => {
    if (gesturingRef.current) return;
    applyCameraDom(pan, zoom);
  }, [pan, zoom, applyCameraDom]);

  const scheduleWheelCommit = useCallback(() => {
    if (wheelCommitRef.current) window.clearTimeout(wheelCommitRef.current);
    wheelCommitRef.current = window.setTimeout(() => {
      wheelCommitRef.current = null;
      gesturingRef.current = false;
      commitCamera();
    }, WHEEL_COMMIT_MS);
  }, [commitCamera]);

  const onWheel = useCallback((e) => {
    e.preventDefault();
    const el = containerRef?.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;
    const factor = e.deltaY > 0 ? 0.92 : 1.08;
    const prev = zoomRef.current;
    const next = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, prev * factor));
    if (next === prev) return;
    const worldX = (mx - panRef.current.x) / prev;
    const worldY = (my - panRef.current.y) / prev;
    const newPan = {
      x: mx - worldX * next,
      y: my - worldY * next,
    };
    cancelCameraAnim();
    gesturingRef.current = true;
    applyCameraDom(newPan, next);
    scheduleWheelCommit();
  }, [containerRef, cancelCameraAnim, applyCameraDom, scheduleWheelCommit]);

  useEffect(() => {
    const el = containerRef?.current;
    if (!el) return undefined;
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [containerRef, onWheel]);

  const onPointerDown = useCallback((e) => {
    if (e.button !== 0 && e.button !== 1) return;
    if (e.target.closest('[data-atlas-no-pan]')) return;
    cancelCameraAnim();
    if (wheelCommitRef.current) {
      window.clearTimeout(wheelCommitRef.current);
      wheelCommitRef.current = null;
    }
    didPanRef.current = false;
    dragRef.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      origX: panRef.current.x,
      origY: panRef.current.y,
      moved: false,
    };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  }, [cancelCameraAnim]);

  const onPointerMove = useCallback((e) => {
    const d = dragRef.current;
    if (!d || d.pointerId !== e.pointerId) return;
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    if (Math.abs(dx) + Math.abs(dy) > 3) {
      d.moved = true;
      didPanRef.current = true;
      gesturingRef.current = true;
    }
    applyCameraDom({ x: d.origX + dx, y: d.origY + dy }, zoomRef.current);
  }, [applyCameraDom]);

  const onPointerUp = useCallback((e) => {
    const d = dragRef.current;
    if (!d || d.pointerId !== e.pointerId) return;
    dragRef.current = null;
    gesturingRef.current = false;
    if (d.moved) commitCamera();
    try {
      e.currentTarget.releasePointerCapture?.(e.pointerId);
    } catch (_) {
      /* ignore */
    }
  }, [commitCamera]);

  const fitToBounds = useCallback((bounds, padding = 48, { animate = false } = {}) => {
    const el = containerRef?.current;
    if (!el || !bounds) return;
    const w = el.clientWidth;
    const h = el.clientHeight;
    const bw = Math.max(1, bounds.maxX - bounds.minX);
    const bh = Math.max(1, bounds.maxY - bounds.minY);
    const z = Math.min(
      FIT_MAX_ZOOM,
      Math.max(MIN_ZOOM, Math.min((w - padding * 2) / bw, (h - padding * 2) / bh))
    );
    const next = {
      x: (w - bw * z) / 2 - bounds.minX * z,
      y: (h - bh * z) / 2 - bounds.minY * z,
    };
    if (animate) animateCamera(next, z);
    else applyCamera(next, z);
  }, [containerRef, animateCamera, applyCamera]);

  const resetView = useCallback((bounds, { animate = false } = {}) => {
    const z = DEFAULT_ZOOM;
    const pad = 40;
    const el = containerRef?.current;
    let next = { x: pad, y: pad };
    if (el && bounds) {
      const w = el.clientWidth;
      const h = el.clientHeight;
      const bw = Math.max(1, bounds.maxX - bounds.minX);
      const bh = Math.max(1, bounds.maxY - bounds.minY);
      const contentW = bw * z;
      const contentH = bh * z;
      next = {
        x: contentW <= w ? (w - contentW) / 2 - bounds.minX * z : pad - bounds.minX * z,
        y: contentH <= h ? (h - contentH) / 2 - bounds.minY * z : pad - bounds.minY * z,
      };
    }
    if (animate) animateCamera(next, z);
    else applyCamera(next, z);
  }, [containerRef, animateCamera, applyCamera]);

  const ensureRectInView = useCallback((rect, pad = 56) => {
    const el = containerRef?.current;
    if (!el || !rect) return;
    const w = el.clientWidth;
    const h = el.clientHeight;
    const z = zoomRef.current;
    const p = panRef.current;
    const left = p.x + rect.x * z;
    const top = p.y + rect.y * z;
    const right = left + rect.width * z;
    const bottom = top + rect.height * z;
    if (right > pad && bottom > pad && left < w - pad && top < h - pad) return;
    const next = {
      x: w / 2 - (rect.x + rect.width / 2) * z,
      y: h / 2 - (rect.y + rect.height / 2) * z,
    };
    applyCamera(next, z);
  }, [containerRef, applyCamera]);

  const setPanFromUser = useCallback((next) => {
    cancelCameraAnim();
    applyCameraDom(next, zoomRef.current);
    setPan(next);
  }, [cancelCameraAnim, applyCameraDom]);

  const setPanLive = useCallback((next) => {
    cancelCameraAnim();
    gesturingRef.current = true;
    applyCameraDom(next, zoomRef.current);
  }, [cancelCameraAnim, applyCameraDom]);

  const endPanLive = useCallback(() => {
    gesturingRef.current = false;
    commitCamera();
  }, [commitCamera]);

  return {
    pan,
    zoom,
    panRef,
    zoomRef,
    cameraRef,
    gridRef,
    setPan: setPanFromUser,
    setPanLive,
    endPanLive,
    setZoom,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    fitToBounds,
    resetView,
    ensureRectInView,
    didPanRef,
  };
}
